<template>
  <WarRoomGrid :sticky-sidebar="!$device.isMobile" class="war-room-posts-page">
    <template v-if="$device.isMobile && isJobsBannerVisible" #before>
      <WarRoomJobApplicationBanner @close="handleJobsBannerClose" />
    </template>

    <template #content>
      <div data-test="posts_list_content">
        <WarRoomPostButton v-if="quickCreateVisible" class="create-post-mobile__button" />

        <WarRoomJobApplicationBanner v-if="!$device.isMobile && isJobsBannerVisible" @close="handleJobsBannerClose" />
        <WarRoomPostListHeader class="war-room-posts-page__header" data-test="posts_list_header" />
        <WarRoomPostListObserver>
          <BravadoInfiniteScrollOffset
            v-slot="{ getScrollMeta }"
            :key="$route.query.tab"
            :pagination="pagination"
            :page="currentPage"
            :scroll-to="feedScrollMeta"
            @scroll:init="setFeedScrollMeta({})"
            @load="handleLoad"
          >
            <template v-if="loading">
              <WarRoomPostPreviewPlaceholder
                v-for="p in posts.length"
                :key="p"
                class="war-room-posts-page__placeholder"
              />
            </template>
            <template v-else>
              <WarRoomAchievementBanner class="war-room-posts-page__achievement-banner" />
              <template v-if="isPinnedPostVisible">
                <WarRoomPinnedPosts
                  :pinned-posts="groupedPosts.pinned"
                  class="war-room-posts-page__preview"
                  @on-click-post="sendClickEvent"
                />
              </template>
              <template v-for="(post, index) in groupedPosts.unpinned">
                <WarRoomPostPreview
                  :key="post.slug"
                  :post="post"
                  data-test="posts_list_preview"
                  :data-page="post.page"
                  :data-post-slug="post.slug"
                  class="war-room-posts-page__preview"
                  @click.native="handleClickPost(post, getScrollMeta)"
                  @contextmenu.native="handleClickPost(post, getScrollMeta, true)"
                />
                <ClientOnly
                  v-if="isSidebarBlockVisible(index, 1) && $auth.loggedIn"
                  :key="`sidebar-component-${index}`"
                >
                  <WarRoomProfileBanner :class="sideBarComponentClass" />
                </ClientOnly>
                <ClientOnly v-if="isSidebarBlockVisible(index, 2)" :key="`sidebar-component-${index}`">
                  <WarRoomCommissionEarnBanner :class="sideBarComponentClass" />
                </ClientOnly>
              </template>
            </template>
          </BravadoInfiniteScrollOffset>
        </WarRoomPostListObserver>
      </div>
    </template>
    <template #sidebar>
      <WarRoomPostListSidebar v-if="$auth.loggedIn" data-test="posts_list_sidebar" />
      <template v-else>
        <ClientOnly>
          <WarRoomMobileAppBannerAside class="war-room-posts-page__mobile-app-banner" />
          <SidebarFooter />
        </ClientOnly>
      </template>
    </template>
  </WarRoomGrid>
</template>

<script lang="ts">
import { defineComponent } from '@nuxtjs/composition-api';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { DEFAULT_FEED_ROUTE } from './constants';
import BravadoInfiniteScrollOffset from '@/components/common/BravadoInfiniteScroll/BravadoInfiniteScrollOffset.vue';
import SidebarFooter from '@/components/global/SidebarFooter/SidebarFooter.vue';
import WarRoomAchievementBanner from '@/components/partials/war-room/WarRoomAchievementBanner.vue';
import WarRoomCommissionEarnBanner from '@/components/partials/war-room/WarRoomCommissionEarnBanner/WarRoomCommissionEarnBanner.vue';
import WarRoomGrid from '@/components/partials/war-room/WarRoomGrid/WarRoomGrid.vue';
import WarRoomJobApplicationBanner from '@/components/partials/war-room/WarRoomJobApplicationBanner/WarRoomJobApplicationBanner.vue';
import WarRoomPinnedPosts from '@/components/partials/war-room/WarRoomPinnedPosts/WarRoomPinnedPosts.vue';
import WarRoomMobileAppBannerAside from '@/components/partials/war-room/WarRoomMobileAppBanner/WarRoomMobileAppBannerAside/WarRoomMobileAppBannerAside.vue';
import WarRoomPostListHeader from '@/components/partials/war-room/WarRoomPostListHeader/WarRoomPostListHeader.vue';
import WarRoomPostListObserver from '@/components/partials/war-room/WarRoomPostListObserver.vue';
import WarRoomPostListSidebar from '@/components/partials/war-room/WarRoomPostListSidebar/WarRoomPostListSidebar.vue';
import WarRoomPostPreview from '@/components/partials/war-room/WarRoomPostPreview.vue';
import WarRoomPostPreviewPlaceholder from '@/components/partials/war-room/WarRoomPostPreviewPlaceholder.vue';
import WarRoomJobApplicationMixin from '@/components/partials/war-room/WarRoomJobApplicationBanner/WarRoomJobApplicationMixin';
import WarRoomProfileBanner from '@/components/partials/war-room/WarRoomProfileBanner/WarRoomProfileBanner.vue';
import { groupBy } from '@/components/utils/core/collection';
import type { IPost } from '@/types/community';
import { HIDE_CONTENT_FROM_SPECIAL_USERS_AFTER_DATE } from '@/constants';

const SIDEBAR_COMPONENT_INSERT_COUNT = 4;

export default defineComponent({
  name: 'WarRoomPostList',
  components: {
    BravadoInfiniteScrollOffset,
    SidebarFooter,
    WarRoomAchievementBanner,
    WarRoomCommissionEarnBanner,
    WarRoomGrid,
    WarRoomJobApplicationBanner,
    WarRoomMobileAppBannerAside,
    WarRoomPinnedPosts,
    WarRoomPostListHeader,
    WarRoomPostListObserver,
    WarRoomPostListSidebar,
    WarRoomPostPreview,
    WarRoomPostPreviewPlaceholder,
    WarRoomProfileBanner,
    WarRoomPostButton: () => import('@/components/partials/war-room/WarRoomPostButton.vue'),
  },
  mixins: [WarRoomJobApplicationMixin],
  computed: {
    ...mapState('warRoom', ['loading', 'feedScrollMeta']),
    ...mapGetters('warRoom', ['posts', 'pagination']),
    quickCreateVisible(): boolean {
      return this.$auth.loggedIn && this.$route.name === DEFAULT_FEED_ROUTE;
    },
    settings() {
      return this.$store.state.settings;
    },
    groupedPosts() {
      const grouped = groupBy(this.filteredPosts, (post: IPost) => (post.pinned ? 'pinned' : 'unpinned'));
      return {
        pinned: grouped.pinned || [],
        unpinned: grouped.unpinned || [],
      };
    },
    isPinnedPostVisible() {
      if (this.$route.query.tab === 'new' || this.$route.query.tab === 'hot') return false;
      return this.groupedPosts.pinned.length > 0;
    },
    currentPage() {
      return Number(this.$route.query.page) || 1;
    },
    filteredPosts() {
      const { specialUsers } = this.settings;

      const isHide = !specialUsers.includes(this.$auth.user?.username ?? '');

      if (isHide) {
        const hideAfter = HIDE_CONTENT_FROM_SPECIAL_USERS_AFTER_DATE.getTime();

        return this.posts?.filter((post: IPost) => {
          const publishedAt = new Date(post.published_at).getTime();
          const timeDiff = hideAfter - publishedAt;

          return !(specialUsers.includes(post.user_username) && timeDiff < 0);
        });
      }

      return this.posts;
    },
    sideBarComponentClass() {
      return ['war-room-posts-page__preview', 'war-room-posts-page__sidebar-component'];
    },
  },
  beforeMount() {
    window.addEventListener('beforeunload', this.handleJobsBannerCloseOnReload, false);
    window.addEventListener('pagehide', this.handleJobsBannerCloseOnReload);
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.handleJobsBannerCloseOnReload);
    window.removeEventListener('pagehide', this.handleJobsBannerCloseOnReload);
  },
  methods: {
    ...mapMutations('warRoom', ['setFeedScrollMeta']),
    ...mapActions('warRoom', ['fetchMorePosts']),
    isSidebarBlockVisible(index: number, order: number) {
      // Index is zero-based, increment by 1
      let count = index + 1;
      // Count pinned posts as a single post
      count = this.groupedPosts.pinned.length > 0 ? count + 1 : count;
      const { page } = this.$route.query;
      return (
        (!page || page === '1') &&
        !(count % SIDEBAR_COMPONENT_INSERT_COUNT) &&
        count / order === SIDEBAR_COMPONENT_INSERT_COUNT
      );
    },
    handleLoad(page: number, direction: 'up' | 'down') {
      return this.fetchMorePosts({ ...this.$route, query: { ...this.$route.query, page }, direction });
    },
    handleClickPost(
      post: IPost,
      getScrollMeta: (slug: string) => { slug?: string; offsetTop?: number },
      isContextMenu = false,
    ) {
      this.sendClickEvent({ post, isContextMenu });
      this.setFeedScrollMeta(getScrollMeta(post.slug));
    },
    sendClickEvent({ post, isContextMenu }: { post: IPost; isContextMenu: boolean }) {
      const score = isNaN(post.algorithm_metadata?.score ?? NaN) ? -1 : post.algorithm_metadata?.score;
      const feed_algo = post.algorithm_metadata?.algorithm_name;

      this.$analytics.logEvent('war-room__post-feed__post__click', {
        postId: post.id,
        isContextMenu,
        score,
        feed_algo,
      });
    },
  },
});
</script>

<style scoped lang="scss">
.war-room-posts-page {
  &__header {
    &:not(:first-child) {
      margin-top: 12px;
    }
  }

  &__preview,
  &__placeholder {
    & + & {
      margin-top: 12px;
    }
  }

  &__sidebar-component {
    @include mq('sm') {
      display: none;
    }
  }

  &__achievement-banner {
    margin: 12px 0;

    @include mq('sm') {
      margin: 0 0 12px;
    }
  }

  &__mobile-app-banner {
    margin-top: 16px;
  }
}

.create-post-mobile__button {
  position: fixed;
  bottom: 16px;
  right: 16px;
  z-index: 100;

  @include mq('sm') {
    display: none !important;
  }
}
</style>
